import Query from 'devextreme/data/query';
import dotProp from 'dot-prop-immutable';

import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {AuthService} from '@app/services/auth/auth.service';
import {AngularFirestore} from '@angular/fire/compat/firestore';
import {BehaviorSubject, combineLatest, of, Subject} from 'rxjs';
import {distinctUntilChanged, filter, map, switchMap, takeUntil} from 'rxjs/operators';
import {environment} from '@environments/environment';
import {transcodeDate} from '../../../../../@shared/_generalTools/convertDate';

import {IPublicationQuotas, IQuota} from '@app/components/publications/publication-dashboard-v2/quota-builder/quota-builder.service';

export interface IComputedDatas {
  nbResponses: {
    [key: string]: {
      valid: number;
      invalid: number;
    }
  };
  quotaTarget: IQuotaTarget;
  dataSource: {
    [key: string]: {
      [key: string]: {
        caption: string;
        valid: number;
        invalid: number;
        subQuotas: {
          [key: string]: {
            caption: string;
            valid: number;
            invalid: number;
          }
        }
      }
    }
  };
  dxSeries: {};
}

export interface IQuotaTarget {
  [key: string]: {
    [key: string]: {
      valid: { value: number; target: number; };
      invalid: { value: number; target: number; };
      target: { value: number; target: number; };
    };
  };
}

export interface IRawData {
  extBySurvey: {
    [key: number]: {
      valid: [],
      invalid: []
    }
  };
  surveyIds: number[];
  timeStamp: Date;
}

@Injectable({
  providedIn: 'root'
})
export class QuotaCalculatorService {

  private BASE_URL = environment.apiUrl;
  private referenceBaseCollection = environment.firestoreReferencesCollection;
  private appCommunityLogs = environment.firestoreLogCollection;

  public currentDefinitionFlag$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  public quotasDefinition$: BehaviorSubject<IPublicationQuotas> = new BehaviorSubject<IPublicationQuotas>(null);

  public pubUuid$: BehaviorSubject<string> = new BehaviorSubject(null);

  public rawDatas$: BehaviorSubject<IRawData> = new BehaviorSubject<IRawData>(null);
  public data$: BehaviorSubject<IComputedDatas> = new BehaviorSubject<IComputedDatas>(null);

  public isRefreshing$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isLoadingFromS3$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isComputing$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  public canRefreshFromS3$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private _unsubscribeAll: Subject<any>;

  private maxEventDate: Date = null;

  constructor(
    private http: HttpClient,
    private auth: AuthService,
    private afs: AngularFirestore,
  ) {
    this._unsubscribeAll = new Subject();

  }

  //
  //
  //
  computeQuotas() {

    const states = ['valid', 'invalid'];

    const quotas = this.quotasDefinition$.value;
    const rawData = this.rawDatas$.value;
    let finalData = {};
    let quotaTarget: IQuotaTarget = {};

    const allResponses = {
      allSurveys: {
        valid: 0,
        invalid: 0,
      }
    };

    quotas?.quotasIndex?.forEach((aQuotaKey) => {

      rawData?.surveyIds?.forEach((aSurveyId) => {

        if (!allResponses?.[aSurveyId]) {
          allResponses[aSurveyId] = {
            valid: rawData.extBySurvey[aSurveyId]?.valid?.length,
            invalid: rawData.extBySurvey[aSurveyId]?.invalid?.length,
          };
        }
        allResponses.allSurveys = {
          valid: allResponses.allSurveys.valid + rawData.extBySurvey[aSurveyId]?.valid?.length,
          invalid: allResponses.allSurveys.invalid + rawData.extBySurvey[aSurveyId]?.invalid?.length,
        };


        const curQuota: IQuota = quotas.quotas[aQuotaKey];

        if (!finalData[aQuotaKey]) {
          finalData[aQuotaKey] = {
            allSurveys: {
              caption: curQuota.caption,
              valid: 0,
              invalid: 0,
            },
          };
        }
        if (!finalData[aQuotaKey][aSurveyId]) {
          finalData[aQuotaKey][aSurveyId] = {
            caption: curQuota.caption,
            valid: 0,
            invalid: 0,
          };
        }

        const filterExpression = this.getSafeExpression(curQuota); // JSON.parse(curQuota?.rule)?.filterExpression || [];

        states.forEach((aState: 'valid' | 'invalid') => {

          if (rawData.extBySurvey?.[aSurveyId]?.[aState]) {

            // console.log(rawData.extBySurvey[aSurveyId][aState]);
            // console.log(filterExpression);

            const result = Query(rawData.extBySurvey[aSurveyId][aState]).filter(filterExpression).toArray();
            const nb = result?.length || 0;
            finalData[aQuotaKey][aSurveyId][aState] = nb;
            finalData[aQuotaKey].allSurveys[aState] = nb;

            //
            // Targets by quota
            //
            // quotaTarget
            quotaTarget = dotProp.set(
              quotaTarget,
              `${aQuotaKey}.allSurveys.${aState}`,
              this.getTargetByQuota({
                quotaId: aQuotaKey, validInvalid: aState,
                data: finalData, surveyId: 'allSurveys',
              }),
            );
            //
            //
            //


            //
            // SubQuota ?
            //
            const subQuotasKeys = Object.keys(curQuota?.subQuotas?.quotas || {});
            subQuotasKeys.forEach((aSubQuotaKey) => {

              const subQuota: IQuota = curQuota.subQuotas.quotas[aSubQuotaKey];
              const subQuotaFilterExpression = this.getSafeExpression(subQuota); // JSON.parse(subQuota.rule)?.filterExpression;

              // console.log(result);
              // console.log(subQuotaFilterExpression);

              const subResult = Query(result).filter(subQuotaFilterExpression).toArray();

              // console.log(subResult);

              const nbSub = subResult?.length || 0;
              // if (!finalData[aQuotaKey][aSurveyId]?.subQuotas) {
              //   finalData[aQuotaKey][aSurveyId].subQuotas = {};
              // }
              // if (!finalData[aQuotaKey][aSurveyId].subQuotas?.[aSubQuotaKey]) {
              //   finalData[aQuotaKey][aSurveyId].subQuotas[aSubQuotaKey] = {
              //     caption: subQuota.caption,
              //   };
              // }
              // if (!finalData[aQuotaKey].allSurveys?.subQuotas) {
              //   finalData[aQuotaKey].allSurveys.subQuotas = {};
              // }
              // if (!finalData[aQuotaKey].allSurveys.subQuotas?.[aSubQuotaKey]) {
              //   finalData[aQuotaKey].allSurveys.subQuotas[aSubQuotaKey] = {
              //     caption: subQuota.caption,
              //   };
              // }

              // finalData[aQuotaKey][aSurveyId].subQuotas[aSubQuotaKey][aState] = nbSub;
              finalData = dotProp.set(finalData,
                `${aQuotaKey}.${aSurveyId}.subQuotas.${aSubQuotaKey}.${aState}`,
                nbSub
              );

              // finalData[aQuotaKey].allSurveys.subQuotas[aSubQuotaKey][aState] = nbSub;
              finalData = dotProp.set(finalData,
                `${aQuotaKey}.allSurveys.subQuotas.${aSubQuotaKey}.${aState}`,
                finalData?.[aQuotaKey]?.allSurveys?.subQuotas?.[aSubQuotaKey]?.[aState] || 0 + nbSub
              );

              //
              // Targets by quota
              //
              // quotaTarget
              // quotaTarget = dotProp.set(
              //   quotaTarget,
              //   `${aSubQuotaKey}.${aSurveyId}.${aState}`,
              //   this.getTargetByQuota({
              //     quotaId: aSubQuotaKey, validInvalid: aState, parent: aQuotaKey, data: finalData
              //   }),
              // );
              quotaTarget = dotProp.set(
                quotaTarget,
                `${aSubQuotaKey}.allSurveys.${aState}`,
                this.getTargetByQuota({
                  quotaId: aSubQuotaKey, validInvalid: aState,
                  parent: aQuotaKey, data: finalData,
                  surveyId: 'allSurveys',
                }),
              );
              //
              //
              //


            });


          }
        });

      });


    });

    // console.log('Datas computed:', finalData);

    this.data$.next({
      dataSource: finalData,
      quotaTarget,
      nbResponses: allResponses,
      dxSeries: {},
    });
    this.isComputing$.next(false);

  }

  public getTargetByQuota(args: { quotaId: string, validInvalid: 'valid' | 'invalid', parent?: string, data, surveyId: string }) {

    const {nbResponses, quotas} = this.quotasDefinition$.value;
    let amountPath = `${args.quotaId}.${args.surveyId}.${args.validInvalid}`;
    let pcaRespPath = `${args.quotaId}.amount`;
    if (args.parent) {
      amountPath = `${args.parent}.${args.surveyId}.subQuotas.${args.quotaId}.${args.validInvalid}`;
      pcaRespPath = `${args.parent}.subQuotas.quotas.${args.quotaId}.amount`;
    }
    const value = dotProp.get(args.data, amountPath, '-');

    const pcaValue = dotProp.get(quotas, pcaRespPath, 1);

    // console.log('pcaValue=', pcaValue);

    let amountWantedValue: number = nbResponses * pcaValue;

    // console.log('nbResponses', nbResponses);
    // console.log('pcaValue', pcaValue);
    // console.log(`${nbResponses}*${pcaValue}`);
    // console.log('amountWantedValue', amountWantedValue);

    if (args.parent) {
      const pcaValueFromParent = dotProp.get(quotas, `${args.parent}.amount`, 1);
      amountWantedValue = nbResponses * pcaValueFromParent * pcaValue;
    }

    // Arrondir au pallier supérieur (uniquement pour les nombres non entiers)
    amountWantedValue = Math.ceil(amountWantedValue);

    return {
      value,
      target: amountWantedValue,
    };

  }

  private getSafeExpression(quota: IQuota): any {
    try {
      return JSON.parse(quota?.rule || {})?.filterExpression || [];
    } catch (e) {
      return [];
    }
  }

  //
  //
  //
  startListen(pubUuid: string) {

    if (this.pubUuid$.value === pubUuid) {
      return;
    }
    this.pubUuid$.next(pubUuid);

    //
    // Qu'est-ce qui relance le calcul ?
    // --> PubUuid a changé
    // --> definitionFlag a changé
    // --> nouvelles réponses
    //
    combineLatest([
      this.pubUuid$,
      this.currentDefinitionFlag$,
    ])
      .pipe(
        map((result) => {
          const [pubUuid, currentDefinitionFlag] = result;
          return {
            pubUuid,
            currentDefinitionFlag,
          };
        }),
        filter((data) => data.pubUuid !== null),
        filter((data) => data.currentDefinitionFlag !== null),
        distinctUntilChanged(),
        switchMap((result) => {

          this.isRefreshing$.next(true);

          // Est-ce qu'on va chercher de nouvelles données ?
          return combineLatest([
            of(result),
            this.afs.doc<any>(`${this.appCommunityLogs}/${result.pubUuid}`).valueChanges(),
            this.afs.doc<any>(`${this.referenceBaseCollection}/publications/list/${result.pubUuid}`).valueChanges()
          ]);

        }),
        takeUntil(this._unsubscribeAll),
      ).subscribe((data: any) => {

      const [payload, communityLogs, refPub = {quotasDefinition: {}}] = data;
      const {quotasDefinition} = refPub;

      // console.log('Quelque chose a changé !', payload, communityLogs);
      // console.log(communityLogs?.maxEventDate);
      const maxEventDate = transcodeDate(communityLogs?.maxEventDate || '1974/05/10');
      // console.log('[quota-calculatorService] maxEventDate:', maxEventDate);

      this.quotasDefinition$.next(quotasDefinition as IPublicationQuotas);

      if (maxEventDate > this.maxEventDate) {

        //
        // Nouveaux éléments : relance la requête d'extraction des ext -> s3
        // + le télécharge...
        //
        // console.log('[quota-calculatorService] maxEventDate > this.maxEventDate --> enable refresh from S3');

        if (!this.isLoadingFromS3$.value || !this.data$.value) {
          this.canRefreshFromS3$.next(true);
          // this.refreshExtForQuotas(payload.pubUuid);
        }

        // Empêcher le rafraîchissement à chaque changement....
        this.maxEventDate = maxEventDate;
      } else {

        //
        // Relancer le calcul
        //
        this.computeQuotas();
        //
        //

      }


    });


  }



  public stopListen() {

    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
    this.data$.next(null);
    this.rawDatas$.next(null);
    this.pubUuid$.next(null);
    this.currentDefinitionFlag$.next(null);
    this.isLoadingFromS3$.next(false);
    this.maxEventDate = null;
    // console.log('<--- Stop listening quotas...');
  }


  public refreshExtForQuotas(pubUuid: string) {
    if (!pubUuid) {
      pubUuid = this.pubUuid$.value;
    }
    this.isLoadingFromS3$.next(true);
    this.isComputing$.next(true);
    const url = `${this.BASE_URL}/projects/actions`;
    return this.http.post<any>(url, {
      action: 'refreshExtForQuotas',
      params: {
        pubUuid,
      }
    }).subscribe({
      next: response => {
        // console.log(response.data);
        const {s3FileUrl} = response.data.datas;
        this.http.get(`${s3FileUrl}?noCache=${Date.now()}`)
          .pipe()
          .subscribe((data: IRawData) => {
            this.rawDatas$.next(data);
            this.isRefreshing$.next(false);
            this.isLoadingFromS3$.next(false);
            this.canRefreshFromS3$.next(false);
            this.computeQuotas();
          });
      }
    });

  }

}
